import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { 
  AppState, 
  ApiProvider, 
  ChatSession, 
  ChatTemplate, 
  ChatMessage,
  SessionSearchParams,
  PaginatedResult,
  SessionStats,
  DataExport,
  ImportResult
} from '@/types';
import { StorageOptimizer } from '@/utils/storageOptimizer';

// 默认对话模板
const DEFAULT_TEMPLATES: ChatTemplate[] = [
  {
    id: 'greeting-1',
    name: '简单问候',
    description: '测试 API 连通性的简单问候',
    content: '你好！请介绍一下你自己。',
    category: 'greeting',
    tags: ['测试', '问候'],
  },
  {
    id: 'code-1',
    name: '代码解释',
    description: '请求解释一段代码的功能',
    content: '请解释以下代码的功能和作用：\n```python\ndef fibonacci(n):\n    if n <= 1:\n        return n\n    return fibonacci(n-1) + fibonacci(n-2)\n```',
    category: 'code',
    tags: ['编程', '解释'],
  },
  {
    id: 'summary-1',
    name: '文本摘要',
    description: '请求对一段文本进行摘要',
    content: '请对以下文本进行摘要：\n人工智能是研究、开发用于模拟、延伸和扩展人的智能的理论、方法、技术及应用系统的一门新的技术科学。人工智能企图了解智能的实质，并生产出一种新的能以人类智能相似的方式做出反应的智能机器，该领域的研究包括机器人、语言识别、图像识别、自然语言处理和专家系统等。',
    category: 'summary',
    tags: ['摘要', '文本处理'],
  },
  {
    id: 'creative-1',
    name: '创意写作',
    description: '请求进行创意写作',
    content: '请写一个关于未来科技的短篇故事，包含人工智能和太空探索元素。',
    category: 'creative',
    tags: ['创意', '写作'],
  },
  {
    id: 'test-1',
    name: 'API 测试',
    description: '测试 API 功能和参数',
    content: '请用 JSON 格式返回当前的时间和日期，以及一个随机数。',
    category: 'test',
    tags: ['测试', 'API'],
  },
];

// 模型补全规则
const MODEL_COMPLETION_RULES = [
  {
    prefix: 'gpt-4',
    completions: ['gpt-4-turbo-preview', 'gpt-4-1106-preview', 'gpt-4-0613'],
    description: 'OpenAI GPT-4 模型',
  },
  {
    prefix: 'gpt-3.5',
    completions: ['gpt-3.5-turbo-1106', 'gpt-3.5-turbo-0613', 'gpt-3.5-turbo'],
    description: 'OpenAI GPT-3.5 模型',
  },
  {
    prefix: 'claude-3',
    completions: ['claude-3-opus-20240229', 'claude-3-sonnet-20240229', 'claude-3-haiku-20240307'],
    description: 'Anthropic Claude 3 模型',
  },
  {
    prefix: 'claude-2',
    completions: ['claude-2.1', 'claude-2.0'],
    description: 'Anthropic Claude 2 模型',
  },
  {
    prefix: 'gemini',
    completions: ['gemini-pro', 'gemini-pro-vision'],
    description: 'Google Gemini 模型',
  },
];

interface AppStore extends AppState {
  // 供应商管理
  addProvider: (provider: Omit<ApiProvider, 'id' | 'createdAt' | 'updatedAt'>) => string;
  updateProvider: (id: string, updates: Partial<ApiProvider>) => void;
  deleteProvider: (id: string) => void;
  setCurrentProvider: (id: string) => void;
  
  // 对话管理
  addSession: (session: Omit<ChatSession, 'id' | 'createdAt' | 'updatedAt'>) => string;
  updateSession: (id: string, updates: Partial<ChatSession>) => void;
  deleteSession: (id: string) => void;
  setCurrentSession: (id: string) => void;
  addMessageToSession: (sessionId: string, message: Omit<ChatMessage, 'id' | 'timestamp'>) => void;
  
  // 模板管理
  getTemplate: (id: string) => ChatTemplate | undefined;
  addTemplate: (template: Omit<ChatTemplate, 'id'>) => void;
  
  // 模型补全
  getModelCompletions: (partial: string) => string[];
  
  // UI 状态
  toggleSidebar: () => void;
  setActiveTab: (tab: 'config' | 'providers' | 'chat') => void;
  toggleTheme: () => void;
  
  // 工具函数
  generateUniqueName: (baseName: string, existingNames: string[]) => string;
  
  // 对话历史管理（新增）
  searchSessions: (params: SessionSearchParams) => PaginatedResult<ChatSession>;
  getSessionStats: () => SessionStats;
  deleteSessions: (ids: string[]) => void;
  exportSessions: (ids?: string[]) => DataExport;
  importSessions: (data: DataExport) => ImportResult;
  updateSessionTags: (id: string, tags: string[]) => void;
  updateSessionSummary: (id: string, summary: string) => void;
  getRecentSessions: (limit?: number) => ChatSession[];
  
  // 性能优化功能
  optimizeStorage: () => {
    recommendations: string[];
    actions: string[];
    estimatedSavings: string;
  };
  cleanupOldData: (options?: {
    maxAgeDays?: number;
    maxSessions?: number;
    keepImportant?: boolean;
  }) => {
    sessionsToKeep: ChatSession[];
    sessionsToRemove: ChatSession[];
    removedCount: number;
    keptCount: number;
  };
  compressSessions: () => {
    originalSize: string;
    compressedSize: string;
    compressionRatio: number;
    estimatedSavings: string;
  };
  getStorageAnalysis: () => {
    totalSessions: number;
    totalMessages: number;
    storageSize: string;
    cacheStatus: string;
    optimizationLevel: 'low' | 'medium' | 'high';
  };
}

export const useAppStore = create<AppStore>()(
  persist(
    (set, get) => ({
      // 初始状态
      providers: [],
      sessions: [],
      currentProviderId: null,
      currentSessionId: null,
      templates: DEFAULT_TEMPLATES,
      ui: {
        sidebarCollapsed: false,
        activeTab: 'config',
        theme: 'light',
      },

      // 供应商管理
      addProvider: (providerData) => {
        const id = `provider_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const now = new Date().toISOString();
        
        // 检查名称冲突
        const existingNames = get().providers.map(p => p.name);
        const uniqueName = get().generateUniqueName(providerData.name, existingNames);
        
        const provider: ApiProvider = {
          ...providerData,
          id,
          name: uniqueName,
          createdAt: now,
          updatedAt: now,
        };

        set((state) => ({
          providers: [...state.providers, provider],
        }));

        return id;
      },

      updateProvider: (id, updates) => {
        set((state) => ({
          providers: state.providers.map((provider) =>
            provider.id === id
              ? {
                  ...provider,
                  ...updates,
                  updatedAt: new Date().toISOString(),
                }
              : provider
          ),
        }));
      },

      deleteProvider: (id) => {
        set((state) => ({
          providers: state.providers.filter((provider) => provider.id !== id),
          // 如果删除的是当前供应商，清空选择
          currentProviderId: state.currentProviderId === id ? null : state.currentProviderId,
        }));
      },

      setCurrentProvider: (id) => {
        set({ currentProviderId: id });
      },

      // 对话管理
      addSession: (sessionData) => {
        const id = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const now = new Date().toISOString();
        
        const session: ChatSession = {
          ...sessionData,
          id,
          tags: sessionData.tags || [],
          messageCount: sessionData.messages?.length || 0,
          tokenCount: sessionData.messages?.reduce((sum, msg) => sum + (msg.tokens || 0), 0) || 0,
          lastMessageAt: sessionData.messages?.length > 0 
            ? sessionData.messages[sessionData.messages.length - 1].timestamp 
            : now,
          createdAt: now,
          updatedAt: now,
        };

        set((state) => ({
          sessions: [...state.sessions, session],
          currentSession: id,
        }));

        return id;
      },

      updateSession: (id, updates) => {
        set((state) => ({
          sessions: state.sessions.map((session) =>
            session.id === id
              ? {
                  ...session,
                  ...updates,
                  updatedAt: new Date().toISOString(),
                }
              : session
          ),
        }));
      },

      deleteSession: (id) => {
        set((state) => ({
          sessions: state.sessions.filter((session) => session.id !== id),
          currentSessionId: state.currentSessionId === id ? null : state.currentSessionId,
        }));
      },

      setCurrentSession: (id) => {
        set({ currentSessionId: id });
      },

      addMessageToSession: (sessionId, messageData) => {
        const message: ChatMessage = {
          ...messageData,
          id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          timestamp: new Date().toISOString(),
        };

        set((state) => ({
          ...state,
          sessions: state.sessions.map((session) =>
            session.id === sessionId
              ? {
                  ...session,
                  messages: [...session.messages, message],
                  messageCount: session.messageCount + 1,
                  tokenCount: session.tokenCount + (messageData.tokens || 0),
                  lastMessageAt: message.timestamp,
                  updatedAt: new Date().toISOString(),
                }
              : session
          ),
        }));
      },

      // 模板管理
      getTemplate: (id) => {
        return get().templates.find(t => t.id === id);
      },

      addTemplate: (templateData) => {
        const id = `template_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const template: ChatTemplate = {
          ...templateData,
          id,
        };

        set((state) => ({
          templates: [...state.templates, template],
        }));
      },

      // 模型补全
      getModelCompletions: (partial) => {
        const rules = MODEL_COMPLETION_RULES.filter(rule => 
          rule.prefix.toLowerCase().startsWith(partial.toLowerCase()) ||
          partial.toLowerCase().startsWith(rule.prefix.toLowerCase())
        );
        
        return rules.flatMap(rule => rule.completions);
      },

      // UI 状态
      toggleSidebar: () => {
        set((state) => ({
          ui: {
            ...state.ui,
            sidebarCollapsed: !state.ui.sidebarCollapsed,
          },
        }));
      },

      setActiveTab: (tab) => {
        set((state) => ({
          ui: {
            ...state.ui,
            activeTab: tab,
          },
        }));
      },

      toggleTheme: () => {
        set((state) => ({
          ui: {
            ...state.ui,
            theme: state.ui.theme === 'light' ? 'dark' : 'light',
          },
        }));
      },

      // 工具函数
      generateUniqueName: (baseName, existingNames) => {
        if (!existingNames.includes(baseName)) {
          return baseName;
        }

        let counter = 1;
        let newName = `${baseName}_${counter}`;
        
        while (existingNames.includes(newName)) {
          counter++;
          newName = `${baseName}_${counter}`;
        }
        
        return newName;
      },

      // 对话历史管理（新增）
      searchSessions: (params) => {
        const { 
          query = '', 
          providerId, 
          startDate, 
          endDate, 
          tags = [], 
          page = 1, 
          pageSize = 20 
        } = params;
        
        const allSessions = get().sessions;
        
        // 过滤会话
        let filtered = allSessions.filter(session => {
          // 搜索查询匹配
          if (query) {
            const matchesQuery = 
              session.name.toLowerCase().includes(query.toLowerCase()) ||
              session.summary?.toLowerCase().includes(query.toLowerCase()) ||
              session.messages.some(msg => 
                msg.content.toLowerCase().includes(query.toLowerCase())
              );
            if (!matchesQuery) return false;
          }
          
          // 供应商过滤
          if (providerId && session.providerId !== providerId) return false;
          
          // 时间范围过滤
          if (startDate && new Date(session.createdAt) < new Date(startDate)) return false;
          if (endDate && new Date(session.createdAt) > new Date(endDate)) return false;
          
          // 标签过滤
          if (tags.length > 0 && !tags.every(tag => session.tags.includes(tag))) return false;
          
          return true;
        });
        
        // 按时间倒序排序
        filtered.sort((a, b) => 
          new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime()
        );
        
        // 分页
        const startIndex = (page - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        const paginatedItems = filtered.slice(startIndex, endIndex);
        
        return {
          items: paginatedItems,
          pagination: {
            page,
            pageSize,
            total: filtered.length,
          },
        };
      },

      getSessionStats: () => {
        const sessions = get().sessions;
        const providers = get().providers;
        
        const stats: SessionStats = {
          totalSessions: sessions.length,
          totalMessages: sessions.reduce((sum, session) => sum + session.messageCount, 0),
          totalTokens: sessions.reduce((sum, session) => sum + session.tokenCount, 0),
          byProvider: {},
          byDate: {},
          recentActivity: [],
        };
        
        // 按供应商统计
        sessions.forEach(session => {
          const providerName = providers.find(p => p.id === session.providerId)?.name || '未知';
          stats.byProvider[providerName] = (stats.byProvider[providerName] || 0) + 1;
        });
        
        // 按日期统计（最近30天）
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        
        const dateMap: Record<string, { sessions: number; messages: number }> = {};
        sessions.forEach(session => {
          const date = session.createdAt.split('T')[0];
          if (new Date(date) >= thirtyDaysAgo) {
            if (!dateMap[date]) {
              dateMap[date] = { sessions: 0, messages: 0 };
            }
            dateMap[date].sessions += 1;
            dateMap[date].messages += session.messageCount;
          }
        });
        
        // 转换为数组并按日期排序
        stats.recentActivity = Object.entries(dateMap)
          .map(([date, counts]) => ({
            date,
            sessions: counts.sessions,
            messages: counts.messages,
          }))
          .sort((a, b) => b.date.localeCompare(a.date))
          .slice(0, 30);
        
        return stats;
      },

      deleteSessions: (ids) => {
        set((state) => ({
          sessions: state.sessions.filter((session) => !ids.includes(session.id)),
          currentSessionId: state.currentSessionId && ids.includes(state.currentSessionId) 
            ? null 
            : state.currentSessionId,
        }));
      },

      exportSessions: (ids) => {
        const sessions = ids 
          ? get().sessions.filter(session => ids.includes(session.id))
          : get().sessions;
        
        const exportData: DataExport = {
          version: '1.0.0',
          exportedAt: new Date().toISOString(),
          providers: get().providers,
          sessions,
          templates: get().templates,
        };
        
        return exportData;
      },

      importSessions: (data) => {
        const result: ImportResult = {
          success: true,
          importedCount: {
            providers: 0,
            sessions: 0,
            templates: 0,
          },
          errors: [],
          warnings: [],
        };
        
        try {
          // 验证数据格式
          if (!data.version || !data.exportedAt) {
            throw new Error('无效的导出数据格式');
          }
          
          const state = get();
          
          // 导入供应商（避免重复）
          if (data.providers) {
            data.providers.forEach(provider => {
              if (!state.providers.find(p => p.id === provider.id)) {
                state.addProvider(provider);
                result.importedCount.providers++;
              } else {
                result.warnings.push(`供应商 "${provider.name}" 已存在，跳过导入`);
              }
            });
          }
          
          // 导入会话
          if (data.sessions) {
            data.sessions.forEach(session => {
              if (!state.sessions.find(s => s.id === session.id)) {
                state.addSession(session);
                result.importedCount.sessions++;
              } else {
                result.warnings.push(`会话 "${session.name}" 已存在，跳过导入`);
              }
            });
          }
          
          // 导入模板
          if (data.templates) {
            data.templates.forEach(template => {
              if (!state.templates.find(t => t.id === template.id)) {
                state.addTemplate(template);
                result.importedCount.templates++;
              } else {
                result.warnings.push(`模板 "${template.name}" 已存在，跳过导入`);
              }
            });
          }
          
        } catch (error) {
          result.success = false;
          result.errors.push(error instanceof Error ? error.message : '未知错误');
        }
        
        return result;
      },

      updateSessionTags: (id, tags) => {
        set((state) => ({
          sessions: state.sessions.map((session) =>
            session.id === id
              ? {
                  ...session,
                  tags,
                  updatedAt: new Date().toISOString(),
                }
              : session
          ),
        }));
      },

      updateSessionSummary: (id, summary) => {
        set((state) => ({
          sessions: state.sessions.map((session) =>
            session.id === id
              ? {
                  ...session,
                  summary,
                  updatedAt: new Date().toISOString(),
                }
              : session
          ),
        }));
      },

      getRecentSessions: (limit = 10) => {
        const sessions = get().sessions;
        
        return sessions
          .sort((a, b) => 
            new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime()
          )
          .slice(0, limit);
      },

      // 性能优化功能实现
      optimizeStorage: () => {
        const { providers, sessions } = get();
        return StorageOptimizer.optimizeStorage(providers, sessions);
      },

      cleanupOldData: (options = {}) => {
        const { sessions } = get();
        const result = StorageOptimizer.cleanupOldData(sessions, options);
        
        // 应用清理结果
        if (result.sessionsToRemove.length > 0) {
          set({
            sessions: result.sessionsToKeep,
          });
        }
        
        return result;
      },

      compressSessions: () => {
        const { sessions } = get();
        return StorageOptimizer.calculateStorageSize({ sessions });
      },

      getStorageAnalysis: () => {
        const { sessions } = get();
        const totalSessions = sessions.length;
        const totalMessages = sessions.reduce((sum, session) => sum + session.messageCount, 0);
        const sizeInfo = StorageOptimizer.calculateStorageSize({ sessions });
        
        // 检查缓存状态
        let cacheStatus = '未启用';
        try {
          const testKey = 'storage-analysis-test';
          StorageOptimizer.cacheData(testKey, { test: true }, 1000);
          const cached = StorageOptimizer.getCachedData(testKey);
          cacheStatus = cached ? '正常' : '异常';
          localStorage.removeItem(`api-conversation-cache-${testKey}`);
        } catch {
          cacheStatus = '异常';
        }
        
        // 确定优化级别
        let optimizationLevel: 'low' | 'medium' | 'high' = 'low';
        if (totalSessions > 50) {
          optimizationLevel = 'medium';
        }
        if (totalSessions > 200 || totalMessages > 1000) {
          optimizationLevel = 'high';
        }
        if (sizeInfo.compressionRatio < 0.8) {
          optimizationLevel = 'high';
        }
        
        return {
          totalSessions,
          totalMessages,
          storageSize: sizeInfo.originalSize,
          cacheStatus,
          optimizationLevel,
        };
      },
    }),
    {
      name: 'api-conversation-store',
      partialize: (state) => ({
        providers: state.providers,
        sessions: state.sessions,
        templates: state.templates,
        ui: state.ui,
      }),
    }
  )
);